import React, { useContext } from 'react';
import EDTreeSelect from '../../../../../../BaseComponents/TreeSelect/EDTreeSelect';
import { ITreeSection } from '../../../../../../BaseComponents/TreeSelect/TreeSelectUtils';

import {
  IAvenyModelContext,
  ModelContext,
} from '../../../../../../Model/ModelContext/ModelContext';
import shortid from 'shortid';
import {
  getEmptyPage,
  getAttributeSafe,
} from '../../../../../../Components/Urval/Utils/urvalUtils';
import {
  IStringConditionObject,
  IUIPage,
  IUIUrval,
  IValue,
} from '../../../../../../Types/UrvalTypes';
import { TEDSelectionOperator } from '../../../../../../globalQueryTypes';
import { clone } from 'ramda';
import { IResourceInfo } from '../Utils/KalenderTypes';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';

interface IProps {
  dbname: string;
  routineID: number;
  sections: ITreeSection[];
  setPage: (newPage: IUIPage) => void;
  newBookingGroup: string;
}

const NO_RES = '_NoRes';

export const KalenderResourceTreeSelect: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const modelContext = useContext(ModelContext);

  return (
    <EDBox sx={{ margin: -2 }}>
      <EDTreeSelect
        searchValue={''}
        sections={props.sections}
        sectionsUpdate={newSections => {
          // nån har checkat i trädet - uppdatera urvalet
          props.setPage(
            createResursPageFromSections(modelContext, newSections)
          );
        }}
        showSubSectionLines
        parentSectionBoldText
        showPointerLines={2}
        grid
      />
    </EDBox>
  );
};

// Tools

export const recursiveUpdateCheckedResources = (
  sections: ITreeSection[],
  checkedResources: string[]
): ITreeSection[] => {
  return clone(sections).map(section => {
    if (section.subList && section.subList.length > 0) {
      section.subList = recursiveUpdateCheckedResources(
        section.subList,
        checkedResources
      );
    } else {
      section.checked = checkedResources.includes(section.id);
    }
    return section;
  });
};

export const recursiveCreateUrvalValues = (
  sections: ITreeSection[]
): IValue[] => {
  return clone(sections).reduce((values: IValue[], section) => {
    if (section.subList && section.subList.length > 0)
      values = [...values, ...recursiveCreateUrvalValues(section.subList)];
    else if (section.checked)
      values.push({
        id: shortid.generate(),
        value: section.id,
      });
    return values;
  }, []);
};

const createResursPage = (
  modelContext: IAvenyModelContext,
  values: IValue[]
): IUIPage => {
  let page = getEmptyPage();

  let cond: IStringConditionObject = {
    type: 'String',
    id: shortid.generate(),
    attribute: getAttributeSafe(
      'BOResurs',
      values && values.length > 0 ? 'ResursSok' : 'BO_RESURS_ID',
      modelContext.avenyCollectionMap
    ),
    condition: [
      {
        id: shortid.generate(),
        op: TEDSelectionOperator.equals,
        values:
          values && values.length > 0
            ? values
            : [
                {
                  id: shortid.generate(),
                  value: NO_RES,
                },
              ],
      },
    ],
  };

  page.conditionObjects.push(cond);
  return page;
};

export const createResursPageFromSections = (
  modelContext: IAvenyModelContext,
  sections: ITreeSection[]
): IUIPage => {
  const values = recursiveCreateUrvalValues(sections);
  return createResursPage(modelContext, values);
};

export const createResursPageFromResources = (
  modelContext: IAvenyModelContext,
  resources: IResourceInfo[]
): IUIPage => {
  const values = resources.map(item => ({
    id: shortid.generate(),
    value: item.BO_RESURS_ID,
  }));
  return createResursPage(modelContext, values);
};

export const copySectionOpen = (
  newSections: ITreeSection[],
  sectionsWithOpenState: ITreeSection[]
): ITreeSection[] => {
  return newSections.map(newSection => {
    const sectionWithOpenState = sectionsWithOpenState.find(
      section => newSection.value === section.value
    );
    return {
      ...newSection,
      open: sectionWithOpenState ? sectionWithOpenState.open : newSection.open,
      subList: newSection.subList
        ? copySectionOpen(
            newSection.subList,
            sectionWithOpenState?.subList ?? []
          )
        : undefined,
    };
  });
};

export const getCheckedResourcesFromUrval = (
  urval: IUIUrval | null,
  loggedInResource: IResourceInfo | undefined
): string[] => {
  if (urval) {
    let loggedUserId: string[] = [];

    const conditionObject = urval.pages?.[0].conditionObjects?.[0];
    if (conditionObject?.type === 'String') {
      loggedUserId = conditionObject.condition?.[0]?.values.map(
        value => value.value as string
      );
      if (loggedUserId[0] === 'Signatur') {
        loggedUserId.splice(0, 0, loggedInResource?.resId ?? '');
      }
      return loggedUserId;
    } else {
      return [];
    }
  } else {
    return [];
  }
};
